import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { IconButton, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import { CATALOG_NEW_ITEM_ID } from "./catalog/catalogNav";
import {
  useGetBookkeepingAccountsQuery,
  useDeleteBookkeepingAccountMutation,
} from "../query/bookkeepingAccountsQuery";
import type { BookkeepingAccountId } from "@KassaroAB/shared/types/ids";

export const BookkeepingAccountsView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("bookkeepingAccounts");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<BookkeepingAccountId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: accounts,
    isLoading,
    error: loadError,
  } = useGetBookkeepingAccountsQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteBookkeepingAccountMutation(scope);

  const accountList = accounts ?? [];
  const deleteName =
    accountList.find((account) => account.id === deleteId)?.name ?? "";

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("bookkeepingAccountsView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && accountList.length === 0) {
    return (
      <PageContent title={t("bookkeepingAccountsView.title")}>
        <LoadingContainer message={t("bookkeepingAccountsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("bookkeepingAccountsView.title")}
      actions={
        <RouterButtonLink to={`${pagePath}/${CATALOG_NEW_ITEM_ID}`}>
          <Plus size={16} aria-hidden />
          {t("bookkeepingAccountsView.create")}
        </RouterButtonLink>
      }
    >
      {loadError && (
        <InfoBox variant="error">
          {t("bookkeepingAccountsView.errorLoad")}
        </InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">
          {t("bookkeepingAccountsView.errorSave")}
        </InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {accountList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>
                  {t("bookkeepingAccountsView.tableName")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("bookkeepingAccountsView.tableNumber")}
                </TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {accountList.map((account) => (
                <TableRow key={account.id} to={`${pagePath}/${account.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={account.name}
                      origin={account.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>{account.accountNumber}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(account.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState
            message={`${t("bookkeepingAccountsView.empty")} ${t("bookkeepingAccountsView.emptyHint")}`}
          />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("bookkeepingAccountForm.deleteAccount")}
        message={t("bookkeepingAccountsView.deleteConfirm", {
          name: deleteName,
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
